/**
 * MagicBI Design System - Transition Tokens
 * 
 * Animation timing, easing curves, and transition
 * configurations for consistent motion design.
 */

// Duration values (in milliseconds)
export const duration = {
  instant: '0ms',
  fast: '150ms',
  normal: '250ms',
  slow: '350ms',
  slower: '500ms',
  slowest: '750ms',
} as const;

// Easing curves
export const easing = {
  // Standard easing curves
  linear: 'linear',
  ease: 'ease',
  easeIn: 'ease-in',
  easeOut: 'ease-out',
  easeInOut: 'ease-in-out',
  
  // Custom cubic-bezier curves
  smooth: 'cubic-bezier(0.4, 0.0, 0.2, 1)',        // Material Design standard
  snappy: 'cubic-bezier(0.4, 0.0, 0.6, 1)',        // More pronounced ease-out
  bouncy: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)', // Bounce effect
  sharp: 'cubic-bezier(0.4, 0.0, 1, 1)',           // Sharp ease-in
  
  // Specialized curves
  slideIn: 'cubic-bezier(0.0, 0.0, 0.2, 1)',       // For slide-in animations
  slideOut: 'cubic-bezier(0.4, 0.0, 1, 1)',        // For slide-out animations
  fadeIn: 'cubic-bezier(0.0, 0.0, 0.2, 1)',        // For fade-in animations
  fadeOut: 'cubic-bezier(0.4, 0.0, 1, 1)',         // For fade-out animations
} as const;

// Semantic transition tokens
export const transitions = {
  // Interactive element transitions
  interactive: {
    // Button transitions
    button: {
      background: `background-color ${duration.fast} ${easing.smooth}`,
      transform: `transform ${duration.fast} ${easing.smooth}`,
      shadow: `box-shadow ${duration.fast} ${easing.smooth}`,
      all: `all ${duration.fast} ${easing.smooth}`,
    },
    
    // Link transitions
    link: {
      color: `color ${duration.fast} ${easing.smooth}`,
      opacity: `opacity ${duration.fast} ${easing.smooth}`,
    },
    
    // Input transitions
    input: {
      border: `border-color ${duration.fast} ${easing.smooth}`,
      shadow: `box-shadow ${duration.fast} ${easing.smooth}`,
      background: `background-color ${duration.fast} ${easing.smooth}`,
    },
  },
  
  // Layout transitions
  layout: {
    // Collapse/expand
    collapse: {
      height: `height ${duration.normal} ${easing.smooth}`,
      opacity: `opacity ${duration.fast} ${easing.smooth}`,
    },
    
    // Slide transitions
    slide: {
      transform: `transform ${duration.normal} ${easing.slideIn}`,
      all: `all ${duration.normal} ${easing.slideIn}`,
    },
    
    // Fade transitions
    fade: {
      opacity: `opacity ${duration.normal} ${easing.fadeIn}`,
      all: `all ${duration.normal} ${easing.fadeIn}`,
    },
  },
  
  // Modal and overlay transitions
  overlay: {
    // Modal backdrop
    backdrop: {
      opacity: `opacity ${duration.normal} ${easing.smooth}`,
    },
    
    // Modal content
    modal: {
      transform: `transform ${duration.normal} ${easing.smooth}`,
      opacity: `opacity ${duration.normal} ${easing.smooth}`,
      scale: `transform ${duration.normal} ${easing.bouncy}`,
    },
    
    // Tooltip/popover
    popover: {
      opacity: `opacity ${duration.fast} ${easing.smooth}`,
      transform: `transform ${duration.fast} ${easing.smooth}`,
    },
    
    // Dropdown
    dropdown: {
      opacity: `opacity ${duration.fast} ${easing.smooth}`,
      transform: `transform ${duration.fast} ${easing.slideIn}`,
    },
  },
  
  // Page transitions
  page: {
    // Page navigation
    navigation: {
      all: `all ${duration.normal} ${easing.smooth}`,
    },
    
    // Loading states
    loading: {
      opacity: `opacity ${duration.slow} ${easing.smooth}`,
    },
  },
} as const;

// Animation keyframes
export const animations = {
  // Fade animations
  fadeIn: {
    name: 'fadeIn',
    keyframes: `
      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
    `,
    duration: duration.normal,
    easing: easing.fadeIn,
  },
  
  fadeOut: {
    name: 'fadeOut',
    keyframes: `
      @keyframes fadeOut {
        from {
          opacity: 1;
        }
        to {
          opacity: 0;
        }
      }
    `,
    duration: duration.normal,
    easing: easing.fadeOut,
  },
  
  // Slide animations
  slideInUp: {
    name: 'slideInUp',
    keyframes: `
      @keyframes slideInUp {
        from {
          transform: translateY(100%);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }
    `,
    duration: duration.normal,
    easing: easing.slideIn,
  },
  
  slideInDown: {
    name: 'slideInDown',
    keyframes: `
      @keyframes slideInDown {
        from {
          transform: translateY(-100%);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }
    `,
    duration: duration.normal,
    easing: easing.slideIn,
  },
  
  slideInLeft: {
    name: 'slideInLeft',
    keyframes: `
      @keyframes slideInLeft {
        from {
          transform: translateX(-100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
    `,
    duration: duration.normal,
    easing: easing.slideIn,
  },
  
  slideInRight: {
    name: 'slideInRight',
    keyframes: `
      @keyframes slideInRight {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
    `,
    duration: duration.normal,
    easing: easing.slideIn,
  },
  
  // Scale animations
  scaleIn: {
    name: 'scaleIn',
    keyframes: `
      @keyframes scaleIn {
        from {
          transform: scale(0.95);
          opacity: 0;
        }
        to {
          transform: scale(1);
          opacity: 1;
        }
      }
    `,
    duration: duration.fast,
    easing: easing.smooth,
  },
  
  scaleOut: {
    name: 'scaleOut',
    keyframes: `
      @keyframes scaleOut {
        from {
          transform: scale(1);
          opacity: 1;
        }
        to {
          transform: scale(0.95);
          opacity: 0;
        }
      }
    `,
    duration: duration.fast,
    easing: easing.smooth,
  },
  
  // Loading animations
  spin: {
    name: 'spin',
    keyframes: `
      @keyframes spin {
        from {
          transform: rotate(0deg);
        }
        to {
          transform: rotate(360deg);
        }
      }
    `,
    duration: '1000ms',
    easing: easing.linear,
  },
  
  pulse: {
    name: 'pulse',
    keyframes: `
      @keyframes pulse {
        0%, 100% {
          opacity: 1;
        }
        50% {
          opacity: 0.5;
        }
      }
    `,
    duration: '2000ms',
    easing: easing.easeInOut,
  },
  
  // Bounce animation
  bounce: {
    name: 'bounce',
    keyframes: `
      @keyframes bounce {
        0%, 20%, 53%, 80%, 100% {
          transform: translate3d(0, 0, 0);
        }
        40%, 43% {
          transform: translate3d(0, -30px, 0);
        }
        70% {
          transform: translate3d(0, -15px, 0);
        }
        90% {
          transform: translate3d(0, -4px, 0);
        }
      }
    `,
    duration: '1000ms',
    easing: easing.bouncy,
  },
} as const;

// Type definitions
export type Duration = typeof duration;
export type Easing = typeof easing;
export type Transitions = typeof transitions;
export type Animations = typeof animations;

// Utility functions
export const getDuration = (speed: keyof Duration) => duration[speed];
export const getEasing = (curve: keyof Easing) => easing[curve];

export const createTransition = (
  property: string,
  speed: keyof Duration = 'normal',
  curve: keyof Easing = 'smooth'
) => {
  return `${property} ${duration[speed]} ${easing[curve]}`;
};

export const createAnimation = (
  name: keyof Animations,
  iterationCount: number | 'infinite' = 1,
  fillMode: 'none' | 'forwards' | 'backwards' | 'both' = 'forwards'
) => {
  const animation = animations[name];
  return `${animation.name} ${animation.duration} ${animation.easing} ${iterationCount} ${fillMode}`;
};

// CSS helper for styled-components
export const transitionMixin = {
  // Common transitions
  smooth: (property: string = 'all') => `
    transition: ${createTransition(property, 'normal', 'smooth')};
  `,
  
  fast: (property: string = 'all') => `
    transition: ${createTransition(property, 'fast', 'smooth')};
  `,
  
  slow: (property: string = 'all') => `
    transition: ${createTransition(property, 'slow', 'smooth')};
  `,
  
  // Interactive transitions
  interactive: () => `
    transition: ${transitions.interactive.button.all};
  `,
  
  hover: () => `
    transition: ${transitions.interactive.button.background}, 
                ${transitions.interactive.button.transform};
  `,
  
  focus: () => `
    transition: ${transitions.interactive.input.border}, 
                ${transitions.interactive.input.shadow};
  `,
};

// Animation mixin for styled-components
export const animationMixin = {
  fadeIn: () => `
    ${animations.fadeIn.keyframes}
    animation: ${createAnimation('fadeIn')};
  `,
  
  slideIn: (direction: 'up' | 'down' | 'left' | 'right' = 'up') => {
    const animationName = `slideIn${direction.charAt(0).toUpperCase() + direction.slice(1)}` as keyof Animations;
    return `
      ${animations[animationName].keyframes}
      animation: ${createAnimation(animationName)};
    `;
  },
  
  scale: () => `
    ${animations.scaleIn.keyframes}
    animation: ${createAnimation('scaleIn')};
  `,
  
  spin: () => `
    ${animations.spin.keyframes}
    animation: ${createAnimation('spin', 'infinite')};
  `,
  
  pulse: () => `
    ${animations.pulse.keyframes}
    animation: ${createAnimation('pulse', 'infinite')};
  `,
};
